// The site header: brand, pages, language and the way to enquire. On a phone
// the pages open as a full-width sheet with large tap targets.
import { useEffect, useRef, useState } from 'react';
import { useSite } from '../lib/site-context.jsx';
import { Icon } from './Icon.jsx';

export function Brand({ settings }) {
  return (
    <a href="/" className="wordmark" aria-label={`${settings.company_name || 'Home'}: home`}>
      <span className="keys">{settings.wordmark_part_1 || 'Keys'}</span><span className="grove">{settings.wordmark_part_2 || 'Grove'}</span>
    </a>
  );
}

export function LangToggle() {
  const { lang, setLang } = useSite();
  return (
    <span className="lang-toggle" role="group" aria-label="Language">
      <button type="button" data-lang-toggle="en" className={lang === 'en' ? 'active' : ''} aria-pressed={lang === 'en'} onClick={() => setLang('en')}>EN</button>
      <button type="button" data-lang-toggle="hi" className={lang === 'hi' ? 'active' : ''} aria-pressed={lang === 'hi'} lang="hi" onClick={() => setLang('hi')}>हिं</button>
    </span>
  );
}

export function Header() {
  const { settings, path, nav, t } = useSite();
  const [open, setOpen] = useState(false);
  const toggle = useRef(null);

  // Escape closes the menu; it also closes when a link is followed or the window widens.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); toggle.current?.focus(); } };
    const onResize = () => { if (window.innerWidth >= 900) setOpen(false); };
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    document.documentElement.classList.add('menu-open');
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      document.documentElement.classList.remove('menu-open');
    };
  }, [open]);

  const links = [
    { href: '/', key: 'nav_home', label: 'Home', match: (p) => p === '/' },
    { href: '/projects', key: 'nav_projects', label: 'Projects', match: (p) => p === '/projects' || p === '/project' },
    { href: '/compare', key: 'nav_compare', label: 'Compare', match: (p) => p === '/compare' },
    ...(nav?.blog ? [{ href: '/blog', key: 'nav_blog', label: 'Blog', match: (p) => p === '/blog' || p === '/blog-post' }] : []),
    { href: '/contact', key: 'nav_contact', label: 'Contact', match: (p) => p === '/contact' },
  ];

  return (
    <header className="site-header">
      <div className="wrap header-row">
        <Brand settings={settings} />
        <nav className="main-nav" id="site-nav" aria-label="Main" data-open={open ? 'true' : 'false'}>
          {links.map((l) => (
            <a key={l.href} href={l.href} className={l.match(path) ? 'active' : undefined} aria-current={l.match(path) ? 'page' : undefined} onClick={() => setOpen(false)}>{t(l.key, l.label)}</a>
          ))}
          <div className="nav-tools">
            <LangToggle />
            <a className="btn btn-rust nav-cta" href={path === '/' ? '#enquire' : '/#enquire'} onClick={() => setOpen(false)}>{t('nav_enquire', 'Enquire')}</a>
          </div>
        </nav>
        <button ref={toggle} type="button" className="nav-toggle" aria-label={open ? t('nav_close', 'Close menu') : t('nav_menu', 'Menu')} aria-expanded={open} aria-controls="site-nav" onClick={() => setOpen((o) => !o)}>
          <Icon name={open ? 'close' : 'menu'} size={24} />
        </button>
      </div>
    </header>
  );
}
